import React, { useMemo, useState, useCallback } from "react";
import LayoutWrapper from "../../../components/LayoutWrapper";
import HeaderBar from "../../../components/HeaderBar";
import { FaSearch, FaFilter, FaPaperPlane } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";

/**
 * Listing page (ASPX: UnderMaintenanceSpares.aspx)
 * APIs:
 *  GET /api/underSpare/undermaintenanceList?branchId=&itemCode=&itemName=
 *  GET /api/underSpare/um_autoComplete?branchId=&type=Code|Name&prefix=
 */
const UnderMaintenanceSpares = () => {
  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────
  // LocalStorage values
  // ─────────────────────────────────────────────────────────────
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch =
    localStorage.getItem("branchType") || localStorage.getItem("branch") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const numericBranchId = parseInt(branchId, 10) || 0;

  const [showFilters, setShowFilters] = useState(true);
  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");
  const [source, setSource] = useState("Supplier");

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState({});

  // Autocomplete state — mirrors ASPX AutoCompleteExtender
  const [codeSuggestions, setCodeSuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showCodeDrop, setShowCodeDrop] = useState(false);
  const [showNameDrop, setShowNameDrop] = useState(false);

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  // Mirrors ASPX GetCompletionList1 / GetCompletionList2
  const fetchSuggestions = useCallback(
    async (type, prefix) => {
      if (!prefix || prefix.length < 1) return [];

      try {
        const res = await axios.get(
          `https://nissi-india.com/api/underSpare/um_autoComplete?branchId=${numericBranchId}&type=${type}&prefix=${encodeURIComponent(
            prefix
          )}`,
          {
            headers: commonHeaders,
          }
        );
        return res.data?.data || [];
      } catch {
        return [];
      }
    },
    [numericBranchId, commonHeaders]
  );

  const handleCodeChange = async (val) => {
    setItemCode(val);
    const s = await fetchSuggestions("Code", val);
    setCodeSuggestions(s);
    setShowCodeDrop(s.length > 0);
  };

  const handleNameChange = async (val) => {
    setItemName(val);
    const s = await fetchSuggestions("Name", val);
    setNameSuggestions(s);
    setShowNameDrop(s.length > 0);
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        branchId: String(numericBranchId),
        itemCode,
        itemName,
      });

      const res = await axios.get(
        `https://nissi-india.com/api/underSpare/undermaintenanceList?${qs.toString()}`,
        {
          headers: commonHeaders,
        }
      );

      const list = res.data?.data || [];

      setData(list);
      setSelected({});

      if (list.length === 0) toast.info("No records found.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectAll = (checked) => {
    if (!checked) {
      setSelected({});
      return;
    }

    const map = {};
    data.forEach((r) => {
      if (r?.fld_itemid != null) map[r.fld_itemid] = true;
    });
    setSelected(map);
  };

  const clearFilters = () => {
    setItemCode("");
    setItemName("");
    setSource("Supplier");
    setData([]);
    setSelected({});
    setCodeSuggestions([]);
    setNameSuggestions([]);
    setShowCodeDrop(false);
    setShowNameDrop(false);
  };

  const handleSubmit = () => {
    if (!source) {
      toast.warning("Please select Supplier / Own Work Shop / Scrap");
      return;
    }

    const selectedItems = data.filter((r) => selected[r.fld_itemid]);

    if (selectedItems.length === 0) {
      toast.warning("Please select at least one item.");
      return;
    }

    navigate("/inventory/UnderMaintenanceSparesEntry", {
      state: {
        selectedItems,
        source,
        branchId: numericBranchId,
      },
    });
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="UNDER MAINTENANCE SPARES LISTING" />
      <ToastContainer />

      <div className="p-6 mt-6 rounded-2xl shadow-xl border bg-white text-black">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold">Under Maintenance Spares</h1>
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              {data.length} Items
            </span>
          </div>

          <button
            onClick={() => setShowFilters((s) => !s)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-100 ${
              showFilters ? "bg-gray-100" : ""
            }`}
          >
            <FaFilter className="w-4 h-4" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="rounded-xl p-6 mb-6 border bg-gray-50">
            <h3 className="text-lg font-semibold mb-4">Search Filters</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Item Code with autocomplete */}
              <div className="relative">
                <label className="block text-sm font-medium mb-2">
                  Item Code
                </label>
                <input
                  value={itemCode}
                  autoComplete="off"
                  onChange={(e) => handleCodeChange(e.target.value)}
                  onBlur={() => setTimeout(() => setShowCodeDrop(false), 150)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter item code"
                />
                {showCodeDrop && (
                  <ul className="absolute z-50 bg-white border rounded-lg shadow w-full mt-1 max-h-40 overflow-y-auto text-sm">
                    {codeSuggestions.map((s, i) => (
                      <li
                        key={i}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                        onMouseDown={() => {
                          setItemCode(s);
                          setShowCodeDrop(false);
                        }}
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Item Name with autocomplete */}
              <div className="relative">
                <label className="block text-sm font-medium mb-2">
                  Item Name
                </label>
                <input
                  value={itemName}
                  autoComplete="off"
                  onChange={(e) => handleNameChange(e.target.value)}
                  onBlur={() => setTimeout(() => setShowNameDrop(false), 150)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter item name"
                />
                {showNameDrop && (
                  <ul className="absolute z-50 bg-white border rounded-lg shadow w-full mt-1 max-h-40 overflow-y-auto text-sm">
                    {nameSuggestions.map((s, i) => (
                      <li
                        key={i}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                        onMouseDown={() => {
                          setItemName(s);
                          setShowNameDrop(false);
                        }}
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-3">
                Source Type
              </label>
              <div className="flex flex-wrap gap-6">
                {["Supplier", "Own Work Shop", "Scrap"].map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="source"
                      value={opt}
                      checked={source === opt}
                      onChange={() => setSource(opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Clear Filters
              </button>
              <button
                onClick={handleSearch}
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                <FaSearch className="w-4 h-4" />
                {loading ? "Searching..." : "Search Items"}
              </button>
            </div>
          </div>
        )}

        {/* Selection Counter */}
        {data.length > 0 && (
          <div className="flex justify-between items-center mb-4 p-3 rounded-lg bg-blue-50">
            <span className="text-sm font-medium text-blue-700">
              {selectedCount} of {data.length} items selected
            </span>

            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={selectedCount === data.length && data.length > 0}
                onChange={(e) => selectAll(e.target.checked)}
              />
              Select All
            </label>
          </div>
        )}

        {/* Table */}
        {data.length > 0 ? (
          <div className="rounded-xl border overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase w-12">
                      Select
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      S.No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Item Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Item Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Part No
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {data.map((item, idx) => (
                    <tr
                      key={item.fld_itemid || idx}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={!!selected[item.fld_itemid]}
                          onChange={() => toggleRow(item.fld_itemid)}
                        />
                      </td>
                      <td className="px-4 py-4 text-sm">{idx + 1}</td>
                      <td className="px-4 py-4 text-sm font-medium">
                        {item.fld_itemcode}
                      </td>
                      <td className="px-4 py-4 text-sm">{item.fld_itemname}</td>
                      <td className="px-4 py-4 text-sm">
                        {item.fld_itempartno}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          showFilters && (
            <div className="text-center py-12 text-gray-500">
              <p>No items found. Please use the filters to search.</p>
            </div>
          )
        )}

        {/* Submit */}
        {data.length > 0 && (
          <div className="flex justify-center">
            <button
              onClick={handleSubmit}
              disabled={selectedCount === 0}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                selectedCount === 0
                  ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              <FaPaperPlane className="w-4 h-4" />
              Proceed ({selectedCount})
            </button>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default UnderMaintenanceSpares;